import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLeaf } from '@fortawesome/pro-light-svg-icons';
import { faMessageLines } from '@fortawesome/pro-regular-svg-icons';
import { useJuror } from '../../../../hooks/useAuth';
import { useConfig } from '../../../../hooks/useConfig';
import { ROLES } from '../../../../config/permissions/roles';
import { AWARD_TYPES } from '../../../../models/award';
import { SESSION_TYPES } from '../../../../models/session';
import Submission from '../../../../models/submission';
import { isAwardType } from '../../../../helpers/awardTypes';
import { matchRoles } from '../../../../helpers/roles';
import { combine } from '../../../../helpers/styles';
import EcoCommentModal from './ecoCommentModal';
import styles from './styles.module.scss';

const EcoExpert = ({ submission, updateSubmission, isDetailPage }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { jurorId: impersonatingJurorId } = useParams();
  const { state } = useLocation();
  const { t } = useTranslation();
  const { juror, jurorGroup, role, session } = useJuror();
  const { ENTRIES_TABLE } = useConfig();

  const isCommentPublished = submission.ecoCommentPublished;
  const hasVeto = submission.nominatedVeto;

  const isIconVisible =
    ENTRIES_TABLE.ACTIONS.includes('ecoComment') &&
    isAwardType(AWARD_TYPES.IF_DA, session) &&
    session.sessionType === SESSION_TYPES.FINAL_JURY &&
    (matchRoles(role, [ROLES.staff]) || isCommentPublished || hasVeto);

  const isDisabled =
    (role !== ROLES.chairPerson && submission.chairPersonMustDecide) ||
    (matchRoles(role, [ROLES.staff]) &&
      impersonatingJurorId &&
      impersonatingJurorId !== juror.jurorId);

  const isCommentMarkerVisible = !!submission.ecoComment && !hasVeto;

  const isVetoMarkerVisible = hasVeto;

  const isMarkerVisible = isCommentMarkerVisible || isVetoMarkerVisible;

  function showModal() {
    setIsModalVisible(true);
  }

  function hideModal() {
    setIsModalVisible(false);
  }

  async function handleSave({ comment, isPublished, hasVeto }) {
    const trimmedComment = comment?.trim();

    // If an eco comment should be published, it must be provided
    // Publishing an eco comment is a precondition for setting a veto
    // This way we enforce, that a juror has to provide a comment (and publish it) before setting a veto
    if (!trimmedComment && isPublished) {
      toast.error(
        t('jurorEntries.table.columns.actions.ecoComment.dialog.missingComment')
      );
      return;
    }

    // TODO: Refactor this logic to be reusable. As this is used by several components
    const jurorGroupId =
      !isDetailPage && matchRoles(role, [ROLES.chairPerson, ROLES.staff])
        ? juror.jurorGroups.find(
            (jg) => jg.jurorGroupId === submission?.evaluations[0].jurorGroupId
          )?.jurorGroupId
        : jurorGroup.jurorGroupId;

    const ecoCommentSuccess = await Submission.setEcoComment({
      jurorGroupId: state?.jurorGroupId || jurorGroupId,
      submissionId: submission.submissionId,
      value: { ecoComment: trimmedComment, ecoCommentPublished: isPublished },
    });

    let nominatedVetoSuccess;

    if (submission.nominatedVeto !== hasVeto) {
      nominatedVetoSuccess = await Submission.setNominatedVeto({
        jurorGroupId: state?.jurorGroupId || jurorGroupId,
        submissionId: submission.submissionId,
        value: hasVeto,
      });
    }

    const updatedSubmission = { ...submission };

    if (ecoCommentSuccess) {
      updatedSubmission.ecoComment = trimmedComment;
      updatedSubmission.ecoCommentPublished = isPublished;
    }

    if (nominatedVetoSuccess) {
      updatedSubmission.nominatedVeto = hasVeto;
    }

    updateSubmission(updatedSubmission);

    hideModal();
  }

  async function handleDelete() {
    const jurorGroupId =
      !isDetailPage && matchRoles(role, [ROLES.chairPerson, ROLES.staff])
        ? juror.jurorGroups.find(
            (jg) => jg.jurorGroupId === submission?.evaluations[0].jurorGroupId
          )?.jurorGroupId
        : jurorGroup.jurorGroupId;

    const ecoCommentSuccess = await Submission.deleteEcoComment({
      jurorGroupId: state?.jurorGroupId || jurorGroupId,
      submissionId: submission.submissionId,
    });

    let nominatedVetoSuccess;

    if (submission.nominatedVeto) {
      nominatedVetoSuccess = await Submission.setNominatedVeto({
        jurorGroupId: state?.jurorGroupId || jurorGroupId,
        submissionId: submission.submissionId,
        value: false,
      });
    }

    const updatedSubmission = { ...submission };

    if (ecoCommentSuccess) {
      updatedSubmission.ecoComment = null;
      updatedSubmission.ecoCommentPublished = false;
    }

    if (nominatedVetoSuccess) {
      updatedSubmission.nominatedVeto = false;
    }

    updateSubmission(updatedSubmission);

    hideModal();
  }

  function renderMarker() {
    if (isCommentMarkerVisible) {
      return <FontAwesomeIcon icon={faMessageLines} />;
    }

    if (isVetoMarkerVisible) {
      return '!';
    }
  }

  if (!isIconVisible) return null;

  return (
    <>
      <EcoCommentModal
        visible={isModalVisible}
        onClose={hideModal}
        comment={submission.ecoComment}
        isPublished={submission.ecoCommentPublished}
        hasVeto={submission.nominatedVeto}
        onSave={handleSave}
        onDelete={handleDelete}
      />
      {isIconVisible && (
        <div className={styles.iconContainer}>
          <FontAwesomeIcon
            className={combine(
              styles.icon,
              hasVeto && styles.hasVeto,
              isDisabled && styles.disabled
            )}
            icon={faLeaf}
            size="2x"
            onClick={showModal}
            role="button"
          />
          {isMarkerVisible && (
            <span
              className={combine(
                styles.ecoCommentMarker,
                isDisabled && styles.disabled
              )}
              onClick={showModal}
            >
              {renderMarker()}
            </span>
          )}
        </div>
      )}
    </>
  );
};

EcoExpert.propTypes = {
  submission: PropTypes.object.isRequired,
  updateSubmission: PropTypes.func.isRequired,
  isDetailPage: PropTypes.bool,
};

export default EcoExpert;
